/**
 * Phase 31B: REST polling hook — replaces useSimStream (SSE).
 *
 * Polls GET /api/sim/{runId}/snapshot at 500 ms while running/paused,
 * drops to 2 s after done. On each poll it:
 *   1. Sets `snapshot` (the canonical Phase 31 field)
 *   2. Bridges snapshot → legacy SSE store fields (machines, kpis, activeJobs)
 *      so all existing components (HUD, KpiDashboard, InspectorPanel, GanttPanel)
 *      continue to work without modification.
 */
import { useEffect, useRef } from 'react'
import { useSimStore } from '../store/simStore'
import type { ActiveJobSSE, FactorySnapshot, KpisSSE, MachineStateSSE } from '../types'

// Track which order IDs we've already marked complete so we don't re-fire
const _markedComplete = new Set<string>()

const POLL_RUNNING_MS = 500
const POLL_DONE_MS    = 2_000

export function useSimPoll(runId: string | null) {
  const {
    setSnapshot, setSimTime, setAppState, setFinalKpis, addNotification,
    setKpis, setMachines, setActiveJobs, markOrderComplete, markOrderStarted,
  } = useSimStore()

  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(true)

  useEffect(() => {
    if (!runId) return
    activeRef.current = true
    _markedComplete.clear()  // reset dedup set for each new run

    const poll = async () => {
      if (!activeRef.current) return
      try {
        const res = await fetch(`/api/sim/${runId}/snapshot`)

        if (res.status === 202) {
          // Snapshot not yet available — retry quickly
          timerRef.current = setTimeout(poll, POLL_RUNNING_MS)
          return
        }
        if (!res.ok) {
          // 404 = run gone (server restarted, unknown id) — stop polling
          if (res.status === 404) {
            addNotification('Simulation run not found — please start a new run', 'error')
            setAppState('ERROR')
            return
          }
          addNotification(`Snapshot fetch failed (${res.status})`, 'warn')
          timerRef.current = setTimeout(poll, POLL_DONE_MS)
          return
        }

        let snap: FactorySnapshot
        try {
          snap = await res.json()
        } catch {
          addNotification('Snapshot parse error — retrying…', 'warn')
          timerRef.current = setTimeout(poll, POLL_DONE_MS)
          return
        }
        setSnapshot(snap)
        setSimTime(snap.sim_time)

        // ── Bridge snapshot → legacy SSE store fields ─────────────────────
        // n_total: prefer instanceConfig.n_orders; fall back to snapshot horizon estimate
        const { instanceConfig } = useSimStore.getState()
        const n_total = instanceConfig?.n_orders ?? Math.max(snap.kpis.n_completed, 1)

        // kpis: map KpisSnapshot → KpisSSE (field name fixes included)
        const bridgedKpis: KpisSSE = {
          n_completed:          snap.kpis.n_completed,
          n_late:               snap.kpis.n_late,
          n_total,
          on_time_pct:          snap.kpis.on_time_pct,
          mean_flow:            snap.kpis.mean_flow_time,  // name fix
          mean_tardiness:       snap.kpis.mean_tardiness ?? 0,
          spi:                  snap.kpis.spi,
          throughput_per_hour:  snap.kpis.throughput_per_hour,
        }
        setKpis(bridgedKpis)

        // machines: MachineSnapshot[] → Record<string, MachineStateSSE>
        const machineRecord: Record<string, MachineStateSSE> = {}
        for (const m of snap.machines) {
          machineRecord[m.id] = {
            state:           m.state,
            on_shift:        m.state !== 'off_shift',
            current_order:   m.job_in_service ?? '—',
            queue_depth:     m.jobs_in_queue.length,
            elapsed_pct:     0,
            utilisation_pct: m.utilization * 100,
          }
        }
        setMachines(machineRecord)

        // activeJobs: one entry per machine that has a job in service
        const activeJobsList: ActiveJobSSE[] = snap.machines
          .filter((m) => m.job_in_service != null)
          .map((m) => {
            const order = useSimStore.getState().instanceConfig?.orders.find(
              (o) => o.order_id === m.job_in_service
            )
            return {
              order_id:     m.job_in_service!,
              machine_id:   m.id,
              family:       order?.fg_material ?? '',
              routing_row:  m.routing_row,
              elapsed_pct:  0,
              due_date:     order?.due_date ?? 9999,
              is_late:      order ? (useSimStore.getState().simTime > order.due_date) : false,
              actual_start: m.job_actual_start ?? 0,
            }
          })
        setActiveJobs(activeJobsList)

        // Record actual_start for every order currently on a machine.
        // This persists across routing steps so the Gantt fill never drops
        // out while an order is in transit between machines.
        for (const job of activeJobsList) {
          if (job.actual_start > 0) markOrderStarted(job.order_id, job.actual_start)
        }

        // ── Completed orders → Gantt ──────────────────────────────────────
        for (const co of snap.completed_orders) {
          if (!_markedComplete.has(co.order_id)) {
            _markedComplete.add(co.order_id)
            markOrderComplete(co.order_id, co.sim_time, co.is_late, co.actual_start ?? 0)
          }
        }

        // ── Terminal states ───────────────────────────────────────────────
        if (snap.status === 'done') {
          // Build flat record so ResultsScreen can access fields directly
          setFinalKpis({
            n_completed:         snap.kpis.n_completed,
            n_late:              snap.kpis.n_late,
            n_total,
            on_time_pct:         snap.kpis.on_time_pct,
            mean_flow:           snap.kpis.mean_flow_time,
            spi:                 snap.kpis.spi,
            throughput_per_hour: snap.kpis.throughput_per_hour,
          })
          setAppState('RESULTS')
          return  // stop polling
        }
        if (snap.status === 'error') {
          addNotification('Simulation error', 'error')
          setAppState('ERROR')
          return
        }

        const interval = (snap.status === 'running' || snap.status === 'paused') ? POLL_RUNNING_MS : POLL_DONE_MS
        timerRef.current = setTimeout(poll, interval)

      } catch {
        addNotification('Network error — retrying…', 'warn')
        timerRef.current = setTimeout(poll, POLL_DONE_MS)
      }
    }

    poll()

    return () => {
      activeRef.current = false
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [runId])  // eslint-disable-line react-hooks/exhaustive-deps
}
